import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './EditUser.css';

interface User {
  id: string;
  email: string;
  role: 'participant' | 'organizer' | 'admin';
  name: string;
  phone?: string;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  dietaryRestrictions?: string[];
  preferredAddress?: string;
}

const EditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Editable fields
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredAddress, setPreferredAddress] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [selectedRole, setSelectedRole] = useState<'participant' | 'organizer' | 'admin'>('participant');
  
  // Address search state
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUser(response.data);
      setSelectedRole(response.data.role);
      setName(response.data.name || '');
      setPhone(response.data.phone || '');
      setPreferredAddress(response.data.preferredAddress || '');
      setEmailVerified(response.data.emailVerified || false);
      setIsActive(response.data.isActive !== false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke laste bruker');
    } finally {
      setLoading(false);
    }
  };

  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setIsSearchingAddress(true);
    try {
      const response = await fetch(
        `https://ws.geonorge.no/adresser/v1/sok?sok=${encodeURIComponent(query)}&treffPerSide=10`
      );
      const data = await response.json();
      
      if (data.adresser && data.adresser.length > 0) {
        const suggestions = data.adresser.map((addr: any) => ({
          address: addr.adressetekst,
          municipality: addr.poststed || addr.kommunenavn,
          position: null
        }));
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch (err) {
      console.error('Address search error:', err);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPreferredAddress(value);
    searchAddress(value);
  };

  const selectAddress = (suggestion: any) => {
    const fullAddress = suggestion.municipality 
      ? `${suggestion.address}, ${suggestion.municipality}`
      : suggestion.address;
    setPreferredAddress(fullAddress);
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  const handleSaveChanges = async () => {
    if (!user) return;

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      await axios.patch(
        `${API_URL}/admin/users/${id}`,
        { 
          role: selectedRole,
          name,
          phone,
          preferredAddress,
          emailVerified,
          isActive,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setSuccessMessage('Bruker oppdatert!');
      setUser({ 
        ...user, 
        role: selectedRole,
        name,
        phone,
        preferredAddress,
        emailVerified,
        isActive,
      });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke oppdatere bruker');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Laster bruker...</div>;
  if (error && !user) return <div className="error-message">{error}</div>;
  if (!user) return <div className="error-message">Bruker ikke funnet</div>;

  return (
    <div className="edit-user-page">
      <div className="page-header">
        <Link to="/admin/dashboard" className="back-link">← Tilbake til dashboard</Link>
        <h1>Rediger bruker</h1>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      
      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="edit-user-container">
        {/* Combined User Information */}
        <div className="user-edit-card">
          <h2>Brukerinformasjon</h2>
          
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">E-post:</span>
              <span className="info-value">{user.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Bruker-ID:</span>
              <span className="info-value" style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{user.id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Opprettet:</span>
              <span className="info-value">
                {new Date(user.createdAt).toLocaleString('nb-NO')}
              </span>
            </div>
            {user.lastLogin && (
              <div className="info-item">
                <span className="info-label">Sist innlogget:</span>
                <span className="info-value">
                  {new Date(user.lastLogin).toLocaleString('nb-NO')}
                </span>
              </div>
            )}
          </div>

          <div className="divider"></div>
          
          <div className="form-group">
            <label htmlFor="name">Navn</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Fullt navn"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Telefon</label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+47 123 45 678"
            />
          </div>

          <div className="form-group">
            <label htmlFor="preferredAddress">Foretrukket adresse</label>
            <div className="address-input-wrapper">
              <input
                type="text"
                id="preferredAddress"
                value={preferredAddress}
                onChange={handleAddressChange}
                onFocus={() => preferredAddress.length >= 3 && addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 200)}
                placeholder="Skriv for å søke etter adresse..."
              />
              {isSearchingAddress && <div className="address-loading">Søker...</div>}
              
              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <ul className="address-suggestions">
                  {addressSuggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      onClick={() => selectAddress(suggestion)}
                      className="address-suggestion-item"
                    >
                      <div className="suggestion-address">{suggestion.address}</div>
                      {suggestion.municipality && (
                        <div className="suggestion-municipality">{suggestion.municipality}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={emailVerified}
                onChange={(e) => setEmailVerified(e.target.checked)}
              />
              <span>E-post verifisert</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span>Aktiv bruker</span>
            </label>
          </div>
        </div>

        {/* Role Management */}
        <div className="role-management-card">
          <h2>Rolleadministrasjon</h2>

          <div className="role-selector">
            <p className="role-description">
              Velg brukerrolle for å bestemme hvilke rettigheter brukeren skal ha i systemet.
            </p>

            <div className="role-options">
              <label className={`role-option ${selectedRole === 'participant' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="role"
                  value="participant"
                  checked={selectedRole === 'participant'}
                  onChange={(e) => setSelectedRole(e.target.value as 'participant')}
                />
                <div className="role-content">
                  <div className="role-title">
                    <span className="role-icon">👤</span>
                    <span>Deltaker</span>
                  </div>
                  <p className="role-info">
                    Kan melde seg på arrangementer og delta i Sykkelfester. Standard rolle for alle nye brukere.
                  </p>
                </div>
              </label>

              <label className={`role-option ${selectedRole === 'organizer' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="role"
                  value="organizer"
                  checked={selectedRole === 'organizer'}
                  onChange={(e) => setSelectedRole(e.target.value as 'organizer')}
                />
                <div className="role-content">
                  <div className="role-title">
                    <span className="role-icon">🎯</span>
                    <span>Arrangør</span>
                  </div>
                  <p className="role-info">
                    Kan opprette og administrere egne arrangementer, samt delta som deltaker.
                  </p>
                </div>
              </label>

              <label className={`role-option ${selectedRole === 'admin' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={selectedRole === 'admin'}
                  onChange={(e) => setSelectedRole(e.target.value as 'admin')}
                />
                <div className="role-content">
                  <div className="role-title">
                    <span className="role-icon">👑</span>
                    <span>Administrator</span>
                  </div>
                  <p className="role-info">
                    Full tilgang til systemet. Kan administrere alle brukere, arrangementer og systeminnstillinger.
                  </p>
                </div>
              </label>
            </div>

            <div className="action-buttons">
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/admin/dashboard')}
                disabled={saving}
              >
                Avbryt
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
